"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { priceInsightService } from "@/lib/services/priceInsightService";
import type { SellListingPriceBand } from "@/lib/listing/sellListingValidation";
import { debounce } from "@/lib/utils/debounce";
import { mapPriceSuggestionToSellPriceBand } from "@/lib/pricing/mapPriceSuggestionToSellPriceBand";
import { buildSellPriceBandCacheKey } from "@/lib/pricing/pricingQueryIdentity";
import type { SellPriceBandPolicy } from "@/types/price-insight";
import type {
  CohortFallbackTier,
  TitleCohortMatchMode,
} from "@/lib/pricingTitleCohort";
import type { CombinedPricingProvenance } from "@/lib/pricing/combinedCohortBranchSelection";

export type SellPriceBandSnapshot = SellListingPriceBand & {
  bandPolicy: SellPriceBandPolicy;
  /** Internal — not shown on seller UI (Phase 5). */
  sampleCount: number;
  /** Drives advisory copy on seller UI; comp counts stay server-side (Phase 5). */
  cohortTier?: CohortFallbackTier;
  cohortMatchMode?: TitleCohortMatchMode;
  exactCount?: number;
  cohortCompCount?: number;
  segmentCompCount?: number;
  combinedProvenance?: CombinedPricingProvenance;
};

type SellPriceBandState = {
  fetchKey: string | null;
  data: SellPriceBandSnapshot | null;
  isLoading: boolean;
  error: string | null;
};

const EMPTY_STATE: SellPriceBandState = {
  fetchKey: null,
  data: null,
  isLoading: false,
  error: null,
};

type BandCacheEntry = { snapshot: SellPriceBandSnapshot; cachedAt: number };

/** Session cache; short TTL so server-side pricing fixes show up without a full reload. */
const BAND_CACHE_TTL_MS = 5 * 60 * 1000;
const bandCache = new Map<string, BandCacheEntry>();

function bandCacheKey(
  title: string,
  category: string,
  subcategory: string,
  excludeItemId?: string,
  listingAttributes?: Record<string, unknown> | null,
): string {
  return buildSellPriceBandCacheKey({
    title,
    category,
    subcategory,
    excludeItemId,
    listingAttributes,
  });
}

/** Dev toolbox: prime session band cache for sell form preview scenarios. */
export function primeSellPriceBandCache(
  title: string,
  category: string,
  subcategory: string,
  snapshot: SellPriceBandSnapshot,
): void {
  const t = title.trim();
  const c = category.trim();
  if (!t || !c || t.length < 5) return;
  bandCache.set(bandCacheKey(t, c, subcategory), {
    snapshot,
    cachedAt: Date.now(),
  });
}

export function getCachedSellPriceBand(
  title: string,
  category: string,
  subcategory: string,
): SellPriceBandSnapshot | null {
  const t = title.trim();
  const c = category.trim();
  if (!t || !c || t.length < 5) {
    return null;
  }
  const key = bandCacheKey(t, c, subcategory);
  const cached = bandCache.get(key);
  if (!cached || Date.now() - cached.cachedAt >= BAND_CACHE_TTL_MS) {
    return null;
  }
  return cached.snapshot;
}

export type UseSellPriceBandOptions = {
  /** When true, auto-fetch band when title + category are ready. */
  enabled: boolean;
  title: string;
  category: string;
  subcategory: string;
  debounceMs?: number;
  /** When editing, omit this listing from SIO comp pool stats. */
  excludeItemId?: string;
  /** Structured attrs for query-side cohort (e.g. sewing_machine_type). */
  listingAttributes?: Record<string, unknown> | null;
};

/**
 * Auto-fetches market band for sell flow (no price-for-similarity blend).
 * Callers gate `enabled` via `shouldFetchSellPriceBand` (defer until price step).
 */
export function useSellPriceBand({
  enabled,
  title,
  category,
  subcategory,
  debounceMs = 500,
  excludeItemId,
  listingAttributes,
}: UseSellPriceBandOptions) {
  const [state, setState] = useState<SellPriceBandState>(EMPTY_STATE);

  const trimmedTitle = title.trim();
  const trimmedCategory = category.trim();
  const activeKey =
    enabled && trimmedTitle && trimmedCategory && trimmedTitle.length >= 5
      ? bandCacheKey(
          trimmedTitle,
          trimmedCategory,
          subcategory,
          excludeItemId,
          listingAttributes,
        )
      : null;

  const fetchBand = useCallback(
    async (
      bandTitle: string,
      bandCategory: string,
      bandSubcategory: string,
      bandExcludeItemId: string | undefined,
      bandListingAttributes: Record<string, unknown> | null | undefined,
      options?: { force?: boolean },
    ) => {
      const t = bandTitle.trim();
      const c = bandCategory.trim();
      if (!t || !c || t.length < 5) {
        return;
      }

      const key = bandCacheKey(
        t,
        c,
        bandSubcategory,
        bandExcludeItemId,
        bandListingAttributes,
      );
      if (!options?.force) {
        const cached = bandCache.get(key);
        if (cached && Date.now() - cached.cachedAt < BAND_CACHE_TTL_MS) {
          setState({
            fetchKey: key,
            data: cached.snapshot,
            isLoading: false,
            error: null,
          });
          return;
        }
      }

      setState((prev) => ({
        ...prev,
        fetchKey: key,
        isLoading: true,
        error: null,
      }));

      try {
        const suggestion = await priceInsightService.getPriceSuggestion(
          t,
          c,
          bandSubcategory,
          undefined,
          {
            excludeItemId: bandExcludeItemId,
            listingAttributes: bandListingAttributes,
          },
        );
        const snapshot = mapPriceSuggestionToSellPriceBand(suggestion);
        bandCache.set(key, { snapshot, cachedAt: Date.now() });
        setState({
          fetchKey: key,
          data: snapshot,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error("[useSellPriceBand] fetch failed:", error);
        setState({
          fetchKey: key,
          data: {
            min: 0,
            max: 0,
            enforcementEligible: false,
            bandPolicy: "unavailable",
            sampleCount: 0,
          },
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : "Market price range is unavailable",
        });
      }
    },
    [],
  );

  const debouncedFetch = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const [t, c, s, ex, attrs] = args as [
          string,
          string,
          string,
          string | undefined,
          Record<string, unknown> | null | undefined,
        ];
        void fetchBand(t, c, s, ex, attrs);
      }, debounceMs),
    [fetchBand, debounceMs],
  );

  useEffect(() => {
    if (!activeKey) {
      return;
    }
    debouncedFetch(
      trimmedTitle,
      trimmedCategory,
      subcategory,
      excludeItemId,
      listingAttributes,
    );
  }, [
    activeKey,
    trimmedTitle,
    trimmedCategory,
    subcategory,
    excludeItemId,
    listingAttributes,
    debouncedFetch,
  ]);

  const refetch = useCallback(() => {
    if (!activeKey) {
      return;
    }
    void fetchBand(
      trimmedTitle,
      trimmedCategory,
      subcategory,
      excludeItemId,
      listingAttributes,
      { force: true },
    );
  }, [
    activeKey,
    fetchBand,
    trimmedTitle,
    trimmedCategory,
    subcategory,
    excludeItemId,
    listingAttributes,
  ]);

  const isCurrentFetch = Boolean(activeKey && state.fetchKey === activeKey);
  const isStaleWhileRefetch =
    Boolean(activeKey) && state.fetchKey !== activeKey && state.isLoading;

  return {
    band: isCurrentFetch ? state.data : null,
    isLoading:
      Boolean(activeKey) &&
      (isStaleWhileRefetch || (isCurrentFetch && state.isLoading)),
    error: isCurrentFetch ? state.error : null,
    refetch,
  };
}
